import { Injectable } from "@angular/core";
import { FetchStatusSelector, FetchStatusType } from "@obg/ngrx-utils";
import { memoizeSelector } from "@obg/ngx.global";
import { find, flatMap, isEmpty, isEqual, filter as lodashFilter } from "lodash-es";
import { Observable } from "rxjs";
import { distinctUntilChanged, filter, map } from "rxjs/operators";

import { CommonState } from "../state";

import { ACTION_PREFIX, REWARD_ACTION_TYPE } from "./reward.action";
import {
	Reward,
	RewardCount,
	RewardLockupType,
	Rewards,
	RewardSelectionFilter,
	RewardState,
	RewardStateFilterType,
} from "./reward.model";

/**
 * @deprecated [fabric-deprecated] - use fabric RewardService from app-account instead
 */
// @dynamic
@Injectable({
	providedIn: "root",
})
export class RewardSelector {
	/**
	 * @deprecated [fabric-deprecated] - use fabric RewardService from app-account instead
	 */
	getState() {
		return (state: CommonState): RewardState => state.reward;
	}
	/**
	 * @deprecated [fabric-deprecated] - use fabric RewardService from app-account instead
	 */
	getAll() {
		return (state: CommonState): Rewards => state.reward.rewards;
	}
	/**
	 * @deprecated [fabric-deprecated] - use fabric RewardService from app-account instead
	 */
	isBusy() {
		return (state: CommonState): boolean => FetchStatusSelector.isBusy(ACTION_PREFIX)(state);
	}

	/**
	 * @deprecated [fabric-deprecated] - use fabric RewardService - AccountAppValue.GetRewards from app-account instead
	 */
	getRewards(rewardFilter: RewardStateFilterType[]) {
		return (state: CommonState): Reward[] | undefined => {
			// return undefined, if no rewards are stored in state yet
			let filteredRewards: Reward[] | undefined;
			for (const filterState of rewardFilter) {
				const stateRewards = state.reward.rewards[filterState];
				if (stateRewards) {
					filteredRewards = filteredRewards || [];
					filteredRewards.push(...stateRewards);
				}
			}
			return filteredRewards;
		};
	}

	/**
	 * @deprecated [fabric-deprecated] - use fabric RewardService - AccountAppValue.GetRewardCount from app-account instead
	 */
	@memoizeSelector<CommonState>((state) => [state.reward.rewards])
	getRewardCount() {
		return this.getFilteredRewardCount(() => true);
	}

	/**
	 * @deprecated [fabric-deprecated] - use fabric RewardService - AccountAppValue.GetRewardCount from app-account instead
	 */
	getFilteredRewardCount(predicate: (value: Reward) => boolean) {
		return (state: CommonState): RewardCount => {
			const rewards = state.reward.rewards;
			return {
				claimable: rewards.claimable ? rewards.claimable.filter(predicate).length : 0,
				active:
					rewards.active && rewards.pending
						? rewards.active.filter(predicate).length + rewards.pending.filter(predicate).length
						: 0,
				ended: rewards.ended ? rewards.ended.filter(predicate).length : 0,
			};
		};
	}

	/**
	 * @deprecated [fabric-deprecated] - use fabric RewardService - AccountAppValue.GetRewards from app-account instead
	 */
	getRewards$(rewardFilter: RewardStateFilterType[]) {
		return (state$: Observable<CommonState>): Observable<Reward[]> =>
			state$.pipe(
				distinctUntilChanged(undefined!, (state) => state.reward.rewards),
				map(this.getRewards(rewardFilter)),
				distinctUntilChanged(isEqual)
			);
	}

	/**
	 * @deprecated [fabric-deprecated] - use fabric RewardService - AccountAppValue.GetDepositRewards from app-account instead
	 */
	getDepositRewards(rewardFilter: RewardStateFilterType) {
		return (state: CommonState): Reward[] =>
			lodashFilter(
				state.reward.rewards[rewardFilter],
				(reward) =>
					(reward.objectType === RewardLockupType.LockUpDepositBonusType ||
						reward.objectType === RewardLockupType.PlaytechPokerReward) &&
					!reward.isLockUpBonus
			);
	}

	/**
	 * @deprecated [fabric-deprecated] - use fabric RewardService - AccountAppValue.GetDepositRewards from app-account instead
	 */
	getDepositRewards$(rewardFilter: RewardStateFilterType) {
		return (state$: Observable<CommonState>): Observable<Reward[]> =>
			state$.pipe(
				filter(
					(state) =>
						FetchStatusSelector.isStatus(REWARD_ACTION_TYPE.getAll, FetchStatusType.Success)(state) ||
						FetchStatusSelector.isStatus(REWARD_ACTION_TYPE.getAll, FetchStatusType.Fail)(state)
				),
				distinctUntilChanged(undefined!, (state) => state.reward.rewards),
				map(this.getDepositRewards(rewardFilter)),
				distinctUntilChanged(isEqual)
			);
	}

	/**
	 * @deprecated [fabric-deprecated] - use fabric RewardService - AccountAppValue.GetDepositRewards from app-account instead
	 */
	hasDepositRewards(rewardFilter: RewardStateFilterType) {
		return (state: CommonState): boolean => !isEmpty(this.getDepositRewards(rewardFilter)(state));
	}

	/**
	 * @deprecated [fabric-deprecated] - use fabric RewardService - AccountAppValue.GetRewards from app-account instead
	 */
	getLockUpRewards(rewardFilter: RewardStateFilterType) {
		return (state: CommonState): Reward[] =>
			lodashFilter(
				state.reward.rewards[rewardFilter],
				(reward) =>
					(reward.objectType === RewardLockupType.LockUpDepositBonusType ||
						reward.objectType === RewardLockupType.LockUpLegacyBonusType) &&
					!!reward.isLockUpBonus
			);
	}
	/**
	 * @deprecated [fabric-deprecated] - use fabric RewardService - AccountAppValue.GetRewards from app-account instead
	 */
	getLockUpRewards$(rewardFilter: RewardStateFilterType) {
		return (state$: Observable<CommonState>): Observable<Reward[]> =>
			state$.pipe(
				distinctUntilChanged(undefined!, (state) => state.reward.rewards),
				map(this.getLockUpRewards(rewardFilter)),
				distinctUntilChanged(isEqual)
			);
	}
	/**
	 * @deprecated [fabric-deprecated] - use fabric RewardService - AccountAppValue.GetRewards from app-account instead
	 */
	hasLockUpRewards(rewardFilter: RewardStateFilterType) {
		return (state: CommonState): boolean => !isEmpty(this.getLockUpRewards(rewardFilter)(state));
	}

	/**
	 * @deprecated [fabric-deprecated] - use fabric RewardService - AccountAppValue.GetReward from app-account instead
	 */
	@memoizeSelector<CommonState>((state) => [state.reward.rewards])
	getReward(rewardFilter: RewardSelectionFilter) {
		return (state: CommonState): Reward | undefined => {
			const rewardGroups = state.reward.rewards;

			const rewards = rewardFilter.stateFilter
				? rewardGroups[rewardFilter.stateFilter]
				: (flatMap(rewardGroups) as Reward[]);
			return !isEmpty(rewards) ? find(rewards, { identifier: rewardFilter.identifier }) : undefined;
		};
	}

	/**
	 * @deprecated [fabric-deprecated] - use fabric RewardService - AccountAppValue.GetReward from app-account instead
	 */
	@memoizeSelector<CommonState>((state) => [state.reward.rewards], () => [RewardSelector.prototype.getReward], false)
	getReward$(rewardFilter: RewardSelectionFilter) {
		return (state$: Observable<CommonState>): Observable<Reward | undefined> =>
			state$.pipe(
				filter(
					(state) =>
						FetchStatusSelector.isStatus(REWARD_ACTION_TYPE.get, FetchStatusType.Success)(state) ||
						FetchStatusSelector.isStatus(REWARD_ACTION_TYPE.get, FetchStatusType.Fail)(state)
				),
				distinctUntilChanged(undefined!, (state) => state.reward.rewards),
				map(this.getReward(rewardFilter)),
				distinctUntilChanged(isEqual)
			);
	}
}
